import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LockOpen, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAgentConversations } from '../../../contexts/AgentConversationsContext';
import MessageList from '../MessageList';
import ChatInput from '../ChatInput';
import PatientFocusPill from '../PatientFocusPill';
import { displayTitle, withSummaryDivider } from './conversationPresentation';
import { PanelTopBar, PatientBadge } from './PanelChrome';
import { useConversationActions } from './useConversationActions';

const PatientChip: React.FC<{ id: number; name: string }> = ({ id, name }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`/app/patients/${id}`)}
      title={`Abrir la ficha de ${name}`}
      className="max-w-[150px] shrink-0 cursor-pointer rounded-full border-0 bg-transparent p-0"
    >
      <PatientBadge name={name} />
    </button>
  );
};

const GeneralChip = () => <PatientBadge name={null} />;

// Slot under the header for the patient's clinical context (P2).
export type ThreadExtras = {
  renderPatientContext?: (patient: { id: number; name: string }) => React.ReactNode;
};

// `panel`: docked on the right of a desktop (see CloseButton).
const ConversationThread: React.FC<{ onClose?: () => void; panel?: boolean } & ThreadExtras> = ({
  onClose,
  panel = false,
  renderPatientContext,
}) => {
  const agent = useAgentConversations()!;
  const actions = useConversationActions();
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const renameRef = useRef<HTMLInputElement>(null);

  const { active, activeConversation: conversation, activeTranscript: transcript } = agent;
  const isDraft = active?.kind === 'draft';
  const patient = isDraft ? active.patient : conversation?.patient ?? null;

  const firstUserText = transcript.messages.find((m) => m.role === 'user')?.text;
  const title = conversation ? displayTitle(conversation, firstUserText) : { text: 'Nueva conversación', pending: true };

  const messages = useMemo(
    () => withSummaryDivider(transcript.messages, conversation?.summaryUptoMessageId ?? null),
    [transcript.messages, conversation?.summaryUptoMessageId],
  );

  const startRename = () => {
    setDraftTitle(title.pending ? '' : title.text);
    setRenaming(true);
    requestAnimationFrame(() => renameRef.current?.select());
  };
  const commitRename = async () => {
    const next = draftTitle.trim();
    setRenaming(false);
    if (conversation && next && next !== conversation.title) await actions.rename(conversation, next.slice(0, 120));
  };

  // Pinned to this conversation while another patient's page is open.
  const otherRoutePatient =
    agent.pinned && agent.routePatient && agent.routePatient.id !== patient?.id ? agent.routePatient : null;

  const contextBar = conversation && patient ? renderPatientContext?.(patient) : null;

  const waitingForLoad = !isDraft && !transcript.loaded;
  const inputDisabled =
    agent.activeBusy || agent.authError || agent.connectionState !== 'open' || waitingForLoad;

  return (
    <>
      {/* Same height as the page header (HeaderBar: 64px + its border). The
       *  conversation's own row, the context bar and notices sit below it. */}
      <PanelTopBar
        state={agent.connectionState}
        toReview={agent.unread.size}
        onShowList={agent.showList}
        onNew={() => agent.startDraft(agent.routePatient?.name ? agent.routePatient : null)}
        onClose={onClose}
        panel={panel}
      />
      <div className="box-border flex min-h-11 min-w-0 shrink-0 items-center gap-1 border-b border-border pr-2.5 pl-3">
        {patient ? <PatientChip id={patient.id} name={patient.name} /> : <GeneralChip />}
        {renaming ? (
          <input
            ref={renameRef}
            value={draftTitle}
            maxLength={120}
            aria-label="Nuevo título"
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setRenaming(false);
            }}
            className="h-8 min-w-0 flex-1 rounded-control border border-ring bg-card px-2 text-sm"
          />
        ) : (
          <h2
            title={title.text}
            className={cn(
              'm-0 min-w-0 flex-1 truncate px-1 text-sm',
              title.pending ? 'font-medium text-muted-foreground italic' : 'font-semibold',
            )}
          >
            {title.text}
          </h2>
        )}
        {conversation && !renaming && (
          <Button variant="ghost" size="icon-sm" onClick={startRename} aria-label="Renombrar la conversación">
            <Pencil />
          </Button>
        )}
        {conversation && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={agent.togglePin}
            aria-pressed={agent.pinned}
            aria-label={agent.pinned ? 'Dejar de fijar: seguir la ficha abierta' : 'Fijar esta conversación al navegar'}
            title={agent.pinned ? 'Fijada: no cambia al abrir otra ficha' : 'Sigue la ficha abierta'}
            className={cn(agent.pinned && 'bg-primary-subtle text-primary')}
          >
            {agent.pinned ? <Lock /> : <LockOpen />}
          </Button>
        )}
      </div>

      {(contextBar || conversation?.isLegacy || otherRoutePatient) && (
        <div className="grid gap-2 border-b border-border px-2.5 py-2">
          {contextBar}

          {conversation?.isLegacy && (
            <p className="m-0 rounded-[9px] bg-muted px-2.5 py-1.5 text-[12.5px] text-muted-foreground">
              Antes de las conversaciones por paciente. Puedes seguir escribiendo aquí; para un paciente concreto, mejor su propia conversación.
            </p>
          )}

          {otherRoutePatient && (
            <div className="flex flex-wrap items-center gap-2 rounded-[9px] border border-border bg-muted px-2.5 py-1.5 text-[12.5px]">
              <Lock className="size-3.5 text-muted-foreground" aria-hidden="true" />
              <span>
                {patient ? `Estás en la conversación de ${patient.name}.` : 'Estás en una conversación general.'}
              </span>
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0 text-[12.5px] font-semibold"
                onClick={() => agent.openForPatient(otherRoutePatient)}
              >
                Ir a la de {otherRoutePatient.name || 'este paciente'}
              </Button>
            </div>
          )}
        </div>
      )}

      {agent.authError && (
        <div role="alert" className="border-b border-destructive/30 bg-destructive/10 px-3.5 py-2 text-xs text-destructive">
          Tu sesión ha expirado. Vuelve a iniciar sesión para usar el asistente.
        </div>
      )}

      {isDraft && transcript.messages.length === 0 ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-4.5 py-6">
          <h3 className="m-0 mb-2 text-base font-semibold">
            {patient ? `Nueva conversación sobre ${patient.name}` : 'Nueva conversación general'}
          </h3>
          <p className="m-0 mb-2 text-[13px] text-muted-foreground">
            {patient
              ? `Sissi AI solo trabajará sobre ${patient.name} y tendrá en cuenta su contexto clínico. Después del primer mensaje no se puede cambiar el paciente.`
              : 'En una conversación general Sissi AI puede consultar a cualquier paciente, pero no carga su contexto clínico hasta que lo necesita.'}
          </p>
          <p className="m-0 text-[13px] text-muted-foreground">
            La conversación se guarda al enviar el primer mensaje.
          </p>
        </div>
      ) : transcript.error && !transcript.loaded ? (
        <div role="alert" className="flex-1 px-6 py-9 text-center text-[13.5px] text-muted-foreground">
          <p className="m-0 mb-3">No se pudieron cargar los mensajes.</p>
          {conversation && (
            <Button variant="outline" size="sm" onClick={() => agent.openConversation(conversation.id)}>
              Reintentar
            </Button>
          )}
        </div>
      ) : waitingForLoad && transcript.messages.length === 0 ? (
        <p className="m-0 flex-1 px-6 py-9 text-center text-[13.5px] text-muted-foreground">Cargando mensajes…</p>
      ) : (
        <MessageList
          messages={messages}
          typing={transcript.typing}
          statusLabel={transcript.statusLabel}
          onResolveProposal={agent.resolveProposal}
          onResolveManyProposals={agent.resolveManyProposals}
          onRetrySend={agent.retrySend}
          hasMoreHistory={transcript.hasMore}
          loadingMoreHistory={transcript.loadingMore}
          onLoadMoreHistory={agent.loadOlderMessages}
        />
      )}

      <ChatInput
        disabled={inputDisabled}
        onSend={(text, file) => agent.sendMessage(text, file)}
        header={
          isDraft ? (
            <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <span className="shrink-0">{patient ? 'Sobre:' : 'Conversación general.'}</span>
              {/* The picker's search list spans its container: give it the
               *  rest of the row, not just the width of the pill. */}
              <div className="min-w-0 flex-1">
                <PatientFocusPill
                  focusedPatient={patient}
                  locked={false}
                  hideLock
                  onToggleLock={() => {}}
                  onClear={() => agent.setDraftPatient(null)}
                  onPick={(p) => agent.setDraftPatient(p)}
                />
              </div>
            </div>
          ) : undefined
        }
      />
    </>
  );
};

export default ConversationThread;
